import { onboardingLiveDetailPathOf } from "../../OnboardingLive/OnboardingLivePaths.ts";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { ScreenTitleBlock } from "../../UI/Layout/ScreenTitleBlock.tsx";
import { Card } from "../../UI/Card.tsx";
import { LoadingRows } from "../../UI/Loading/LoadingRows.tsx";
import { InfoFeedback } from "../../UI/Feedback/InfoFeedback.tsx";
import { useCopyToClipboard } from "../../UI/Feedback/useCopyToClipboard.ts";
import { classNames } from "../../UI/Utils/classNames.ts";
import { ClassRoomType, FranchiseeItemFragment } from "../../gql/graphql.ts";
import { useAllFranchisees } from "../useAllFranchisees.ts";
import { TeacherDetailScreenPath } from "../TeacherDetailScreen.tsx";
import { ClassRoomDetailScreenPath } from "../../ClassRoom/ClassRoomDetailScreen.tsx";
import {
  ClassRoomWithMissingFeedback,
  TeacherWithMissingFeedback,
  useTeachersWithMissingFeedback,
} from "./useTeachersWithMissingFeedback.ts";

export const TeacherMissingFeedbackScreenPath = "/teacher-missing-feedbacks";

const linkClassName = "text-sm text-blue-500 underline hover:text-blue-700";

function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function classRoomKindLabel({
  classRoom,
}: ClassRoomWithMissingFeedback): string {
  if (classRoom.type === ClassRoomType.Exam) return "Progress check";
  if (classRoom.is1on1) return "1 to 1 lesson";
  return `Lesson · ${classRoom.topic?.name ?? ""}`;
}

function daysAgoLabel(dateUtc: string): string {
  const days = dayjs()
    .startOf("day")
    .diff(dayjs(dateUtc).startOf("day"), "day");
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

function ClassRoomWithMissingFeedbackItem({
  classRoomWithMissingFeedback,
}: {
  classRoomWithMissingFeedback: ClassRoomWithMissingFeedback;
}) {
  const copyToClipboard = useCopyToClipboard();
  const { classRoom, participantsWithoutFeedback } =
    classRoomWithMissingFeedback;
  const isOnboardingLive = classRoom.type === ClassRoomType.OnboardingLive;
  return (
    <div className={"rounded-lg border border-slate-200 p-3 space-y-1"}>
      <div className={"flex flex-wrap items-baseline justify-between gap-2"}>
        <p className={"font-semibold text-slate-800"}>
          {dayjs(classRoom.startedAtUtc).format("dddd DD MMMM YYYY, HH:mm")}
        </p>
        <span className={"text-xs text-slate-500"}>
          {daysAgoLabel(classRoom.startedAtUtc)}
        </span>
      </div>
      {isOnboardingLive ? (
        <p className={"text-sm text-slate-600 flex items-center gap-2"}>
          <span
            className={
              "inline-flex rounded-full bg-fuchsia-100 px-2 py-0.5 text-xs font-semibold text-fuchsia-700"
            }
          >
            Live onboarding
          </span>
          {classRoom.userGroup.name} · {classRoom.participantsCount} iscritti ·
          feedback del conduttore mancante
        </p>
      ) : (
        <>
          <p className={"text-sm text-slate-600"}>
            {classRoomKindLabel(classRoomWithMissingFeedback)} · Level{" "}
            {classRoom.speakingLevel} · {classRoom.userGroup.name}
          </p>
          <p className={"text-sm text-slate-600"}>
            Students without feedback ({participantsWithoutFeedback.length}/
            {classRoom.participantsCount}):{" "}
            <span className={"font-medium text-slate-800"}>
              {participantsWithoutFeedback
                .map((p) => `${p.givenName} ${p.familyName}`)
                .join(", ")}
            </span>
          </p>
        </>
      )}
      <div className={"flex flex-wrap items-center gap-x-4 gap-y-1 pt-1"}>
        <Link
          to={
            isOnboardingLive
              ? onboardingLiveDetailPathOf(classRoom.id)
              : ClassRoomDetailScreenPath.replace(":classRoomId", classRoom.id)
          }
          className={linkClassName}
        >
          Open class
        </Link>
        <a
          href={classRoom.feedbackUri}
          target={"_blank"}
          rel={"noopener noreferrer"}
          className={classNames(
            linkClassName,
            "inline-flex items-center gap-1",
          )}
        >
          Feedback form
          <ArrowTopRightOnSquareIcon className={"w-4 h-4"} />
        </a>
        <button
          type={"button"}
          onClick={() =>
            copyToClipboard(classRoom.feedbackUri, "Feedback link copied")
          }
          className={linkClassName}
        >
          Copy feedback link
        </button>
      </div>
    </div>
  );
}

function TeacherWithMissingFeedbackCard({
  teacherWithMissingFeedback: { teacherId, classRoomsWithMissingFeedback },
  teacher,
}: {
  teacherWithMissingFeedback: TeacherWithMissingFeedback;
  teacher?: FranchiseeItemFragment;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card>
      <div className={"flex flex-col gap-4"}>
        <button
          type={"button"}
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          className={"flex items-center justify-between gap-3 text-left"}
        >
          <div>
            <p className={"font-semibold text-slate-800"}>
              {teacher
                ? `${teacher.givenName} ${teacher.familyName}`
                : teacherId}
            </p>
            {teacher && (
              <p className={"text-sm text-slate-500"}>{teacher.email}</p>
            )}
          </div>
          <div className={"flex items-center gap-3 shrink-0"}>
            <span
              className={
                "text-sm text-red-600 font-medium bg-red-50 px-3 py-1 rounded-full"
              }
            >
              {pluralize(
                classRoomsWithMissingFeedback.length,
                "class",
                "classes",
              )}{" "}
              without feedback
            </span>
            <ChevronDownIcon
              className={classNames(
                "w-5 h-5 text-slate-500 transition-transform",
                expanded ? "rotate-180" : "",
              )}
            />
          </div>
        </button>
        {expanded && (
          <div className={"flex flex-col gap-3"}>
            <div className={"flex flex-wrap items-center gap-x-4 gap-y-1"}>
              <Link
                to={TeacherDetailScreenPath.replace(":teacherId", teacherId)}
                className={linkClassName}
              >
                Teacher page
              </Link>
              {teacher && (
                <a
                  href={`${import.meta.env.VITE_TEACHER_AREA_APP_BASE_URL}/impersonate-user?uid=${teacher.authId}`}
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                  className={classNames(
                    linkClassName,
                    "inline-flex items-center gap-1",
                  )}
                >
                  Impersonate
                  <ArrowTopRightOnSquareIcon className={"w-4 h-4"} />
                </a>
              )}
            </div>
            {classRoomsWithMissingFeedback.map(
              (classRoomWithMissingFeedback) => (
                <ClassRoomWithMissingFeedbackItem
                  key={classRoomWithMissingFeedback.classRoom.id}
                  classRoomWithMissingFeedback={classRoomWithMissingFeedback}
                />
              ),
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export function TeacherMissingFeedbackScreen() {
  const { teachersWithMissingFeedback, loading } =
    useTeachersWithMissingFeedback();
  const { franchisees } = useAllFranchisees();
  const teacherById = useMemo(
    () => new Map(franchisees.map((f) => [f.id, f])),
    [franchisees],
  );
  const classRoomsWithMissingFeedbackCount = teachersWithMissingFeedback.reduce(
    (count, t) => count + t.classRoomsWithMissingFeedback.length,
    0,
  );

  return (
    <Layout>
      <Card>
        <ScreenTitleBlock
          title={"Missing feedbacks"}
          subTitle={
            <p className={"text-sm text-slate-500"}>
              Teachers who have not given a feedback to every student of their
              lessons and progress checks of the last 30 days
              {teachersWithMissingFeedback.length > 0 &&
                ` · ${pluralize(teachersWithMissingFeedback.length, "teacher", "teachers")}, ${pluralize(classRoomsWithMissingFeedbackCount, "class", "classes")}`}
            </p>
          }
        />
      </Card>
      <div className={"mt-4 flex flex-col gap-4"}>
        {loading && teachersWithMissingFeedback.length <= 0 ? (
          <LoadingRows rows={6} />
        ) : teachersWithMissingFeedback.length > 0 ? (
          teachersWithMissingFeedback.map((teacherWithMissingFeedback) => (
            <TeacherWithMissingFeedbackCard
              key={teacherWithMissingFeedback.teacherId}
              teacherWithMissingFeedback={teacherWithMissingFeedback}
              teacher={teacherById.get(teacherWithMissingFeedback.teacherId)}
            />
          ))
        ) : (
          <InfoFeedback
            title={"All done!"}
            message={"Every teacher gave the feedbacks of the last 30 days"}
          />
        )}
      </div>
    </Layout>
  );
}
